import { ChangeDetectionStrategy, Component, EventEmitter, Input, Output } from '@angular/core';
import { eqProps } from 'ramda';

import { hasValue } from '../../util';
import { SelectionListItemComponent } from './selection-list-item.component';

type CompareWith = (o1, o2) => boolean;
const defaultCompareWith: CompareWith = (o1, o2) =>
  hasValue(o1) && hasValue(o2) && eqProps('id', o1, o2);

/**
 * Wrapper component for selection lists. It should be used if you want to create a list of items that can be selected.
 * In order to create selection list, {@link SelectionListItemComponent} should be used for each selectable option.
 *
 * ex.
 * <pcs-selection-list [selectedItem]="currentlySelectedItem" [compareWith]="compareFunction" (selectionChanged)="onSelectionChanged($event)">
 *   <pcs-selection-list-item *ngFor="let item of items">
 *     <ng-template let-selected="selected" let-selectItem="selectItem">
 *       <div (click)="selectItem(item)" [ngClass]="{'selected': selected}">
 *         {{item.name}}
 *       </div>
 *     </ng-template>
 *   </pcs-selection-list-item>
 * </pcs-selection-list>
 */
@Component({
  selector: 'pcs-selection-list',
  template: '<ng-content></ng-content>',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class SelectionListComponent {
  /**
   * Function that receives two parameters: selectedItem and the item property of {@link SelectionListItemComponent}.
   * It should return true if an item is selected.
   */
  @Input() compareWith = defaultCompareWith;
  /**
   * The value that is currently selected.
   */
  @Input() selectedItem?: any;

  @Output() selectionChanged = new EventEmitter();

  private selectedComponent: SelectionListItemComponent;

  onChange(listItem: SelectionListItemComponent) {
    this.selectedComponent = listItem;
    this.selectionChanged.emit(this.selectedComponent.item);
  }

  selectItem(listItem: SelectionListItemComponent) {
    if (this.selectedComponent) {
      this.selectedComponent.selected = false;
    }

    this.selectedComponent = listItem;
  }

  isSelected(listItem: SelectionListItemComponent) {
    if (!this.selectedItem) {
      return this.selectedComponent === listItem;
    }

    return this.compareWith(this.selectedItem, listItem.item);
  }

}
